import { SavedSettings } from "@bandstand/api/Settings";
import type { SyncedSettings } from "@bandstand/api/Settings";
import { action, computed, observable, reaction, runInAction } from "mobx";

import { ShareError } from "../share/ShareError";
import type { ShareClient } from "../share/ShareClient";
import type { AccountStore } from "./AccountStore";
import type { DisplayStore } from "./DisplayStore";
import type { SettingsStore } from "./SettingsStore";
import type { ThemeStore } from "./ThemeStore";

/** When the settings on this device last changed. Before any change, it's long ago, so the share server's win. */
const CHANGED_KEY = "bandstand.settingsChangedAt";

/** How long to wait after a change before saving, so sliding a slider saves once instead of at every step. */
const WAIT_MS = 1000;

/** How saving the settings on the share server goes. */
export type SyncState = "off" | "saving" | "saved" | "offline" | "failed";

/**
 * Keeps the settings on the share server while someone's signed in, so all their devices look and work the same.
 * Every change is saved there, and the app fetches them when it opens and when it comes back into view. The latest
 * change wins.
 */
export class SettingsSync {
    @observable accessor state: SyncState = "off";

    /** Why the last save didn't work, if it didn't. */
    @observable accessor problem: string | undefined = undefined;

    /** The settings just taken from the share server, so taking them isn't seen as a change to send back. */
    private taken: string | undefined;

    private timer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private readonly share: ShareClient,
        private readonly account: AccountStore,
        private readonly theme: ThemeStore,
        private readonly display: DisplayStore,
        private readonly settings: SettingsStore,
    ) {
        reaction(
            () => JSON.stringify(this.snapshot),
            (snapshot) => {
                if (snapshot === this.taken) return;

                setChangedAt(new Date().toISOString());
                this.saveSoon();
            },
        );

        // Signing in takes the account's settings, when it has any, over what this device had before.
        reaction(
            () => this.account.signedIn,
            () => void this.sync(true),
        );
    }

    /** The settings that travel, as they are on this device. */
    @computed get snapshot(): SyncedSettings {
        const { lyricsOnly, splitSections, sectionSpacing, lineSpacing, chordSize, font } = this.display;

        return {
            theme: this.theme.saved,
            display: { lyricsOnly, splitSections, sectionSpacing, lineSpacing, chordSize, font },
            general: { holdToArrange: this.settings.holdToArrange },
        };
    }

    /**
     * Brings this device and the share server together: whichever changed last wins. Right after signing in, the
     * account's settings win, so a new device looks like the others straight away.
     */
    async sync(justSignedIn = false): Promise<void> {
        if (!this.account.signedIn) return this.setState("off");

        await this.attempt(async () => {
            const saved = await this.share.request("GET", "/settings", undefined, SavedSettings).catch((error: unknown) => {
                if (error instanceof ShareError && error.status === 404) return undefined;

                throw error;
            });

            if (saved && (justSignedIn || Date.parse(saved.changedAt) >= Date.parse(changedAt()))) this.take(saved);
            else await this.save();
        });
    }

    private saveSoon(): void {
        clearTimeout(this.timer);

        if (this.account.signedIn) this.timer = setTimeout(() => void this.attempt(() => this.save()), WAIT_MS);
    }

    /** Sends the settings. When the share server has newer ones, from another device, it answers with those. */
    private async save(): Promise<void> {
        const answer = await this.share.request("PUT", "/settings", { settings: this.snapshot, changedAt: changedAt() }, SavedSettings);

        if (answer.changedAt !== changedAt()) this.take(answer);
    }

    private async attempt(work: () => Promise<void>): Promise<void> {
        this.setState("saving");

        try {
            await work();
            this.setState("saved");
        } catch (error) {
            const offline = error instanceof ShareError && error.status === 0;

            this.setState(offline ? "offline" : "failed", error instanceof Error ? error.message : String(error));
        }
    }

    /** Takes settings from the share server, without counting that as a change on this device. */
    private take(saved: SavedSettings): void {
        runInAction(() => {
            this.theme.apply(saved.settings.theme);
            this.display.apply(saved.settings.display);
            this.settings.apply(saved.settings.general);
            this.taken = JSON.stringify(this.snapshot);
        });

        // The reaction ran as the action ended, so from here on every change is a real one.
        this.taken = undefined;
        setChangedAt(saved.changedAt);
    }

    @action
    private setState(state: SyncState, problem?: string): void {
        this.state = state;
        this.problem = problem;
    }
}

function changedAt(): string {
    try {
        return localStorage.getItem(CHANGED_KEY) ?? new Date(0).toISOString();
    } catch {
        return new Date(0).toISOString();
    }
}

function setChangedAt(time: string): void {
    try {
        localStorage.setItem(CHANGED_KEY, time);
    } catch {
        // Then the share server's settings win next time, which is fine.
    }
}
